import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import Modal from '../../../shared/components/Modal';
import { getGradeLevelLabel } from '../../../shared/constant/gradeLevels';
import { getStudentAttendanceRecords, subjectIdsForGroups } from '../services/attendanceSummaryService';

export const STATUSES = [
  { value: 'present', abbr: 'P', label: 'Present', text: 'text-green-600' },
  { value: 'absent', abbr: 'A', label: 'Absent', text: 'text-red-600' },
  { value: 'late', abbr: 'L', label: 'Late', text: 'text-amber-500' },
  { value: 'excused', abbr: 'E', label: 'Excused', text: 'text-[#4ea6d0]' },
];

const navButton =
  'flex items-center gap-1 rounded-md border border-[#4ea6d0] px-3 py-1.5 text-sm font-medium text-[#0b4a71] hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent';

const formatDate = (date) =>
  new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const formatMonth = (month) =>
  new Date(`${month}-01T00:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

const emptyCounts = () => ({ present: 0, absent: 0, late: 0, excused: 0 });
const totalOf = (c) => c.present + c.absent + c.late + c.excused;
// Late still counts as attended.
const rateOf = (c) => (totalOf(c) > 0 ? `${Math.round(((c.present + c.late) / totalOf(c)) * 100)}%` : '—');

// One student's whole attendance history (every month and day on record, narrowed only to the
// summary page's subject filter): overall totals, a breakdown by month and by subject, and the
// dated log, with Previous/Next to move through the students in the summary. `subjectLink(subjectId,
// student)`, when given, makes each subject in the By Subject table a link (to the teacher's own
// subject page; leave it out for viewers who can't open those).
function StudentAttendanceModal({
  students,
  index,
  subjects,
  statuses = STATUSES,
  subjectName = '',
  subjectLink = null,
  onIndexChange,
  onClose,
}) {
  const student = students[index];
  const cache = useRef({});
  const [records, setRecords] = useState(cache.current[student.id] ?? null);
  const [error, setError] = useState('');
  const [showRecords, setShowRecords] = useState(false);

  const hasPrev = index > 0;
  const hasNext = index < students.length - 1;

  // Subjects taught to this student's section (narrowed to the subject filter, if any).
  const subjectIds = subjectIdsForGroups(subjects, [{ grade_level: student.grade_level, section: student.section }], subjectName);
  const subjectNames = Object.fromEntries(subjects.map((s) => [s.id, s.name]));

  useEffect(() => {
    setError('');
    if (cache.current[student.id]) {
      setRecords(cache.current[student.id]);
      return undefined;
    }

    let mounted = true;
    setRecords(null);
    getStudentAttendanceRecords({ studentId: student.id, subjectIds })
      .then((result) => {
        cache.current[student.id] = result;
        if (mounted) setRecords(result);
      })
      .catch((err) => {
        if (mounted) setError(err.message || 'Failed to load attendance');
      });

    return () => {
      mounted = false;
    };
    // subjectIds is derived from the student and the page's filters, which can't change while open.
  }, [student.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && hasPrev) onIndexChange(index - 1);
      if (e.key === 'ArrowRight' && hasNext) onIndexChange(index + 1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [index, hasPrev, hasNext, onIndexChange]);

  const statusOf = Object.fromEntries(statuses.map((s) => [s.value, s]));

  const overall = emptyCounts();
  // Records come newest first, so months are inserted newest first too.
  const byMonth = new Map();
  // Every subject in scope is listed, even if nothing was recorded for it.
  const bySubject = new Map(
    subjectIds
      .map((id) => [id, { label: subjectNames[id], ...emptyCounts() }])
      .sort((a, b) => a[1].label.localeCompare(b[1].label))
  );
  (records ?? []).forEach((r) => {
    const month = r.date.slice(0, 7);
    if (!byMonth.has(month)) byMonth.set(month, { label: formatMonth(month), ...emptyCounts() });
    overall[r.status] += 1;
    byMonth.get(month)[r.status] += 1;
    if (bySubject.has(r.subjectId)) bySubject.get(r.subjectId)[r.status] += 1;
  });

  const fullName = `${student.lastname}, ${student.firstname} ${student.middlename ?? ''}`.trim();

  // `linkFor(key)` gives the first column's link, if any.
  const renderCountTable = (heading, firstColumn, rows, linkFor = () => null) => (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-[#0b4a71]">{heading}</h3>
      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-[#4ea6d0] text-left text-white">
            <tr>
              <th className="px-4 py-2 font-medium">{firstColumn}</th>
              {statuses.map((s) => (
                <th key={s.value} className="px-4 py-2 font-medium text-center" title={s.label}>
                  {s.abbr}
                </th>
              ))}
              <th className="px-4 py-2 font-medium text-center">Rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(([key, row]) => (
              <tr key={key}>
                <td className="px-4 py-2 font-medium text-[#0b4a71]">
                  {linkFor(key) ? (
                    <Link to={linkFor(key)} className="underline decoration-[#4ea6d0] underline-offset-2 hover:text-[#4ea6d0]">
                      {row.label}
                    </Link>
                  ) : (
                    row.label
                  )}
                </td>
                {statuses.map((s) => (
                  <td key={s.value} className={`px-4 py-2 text-center font-medium ${row[s.value] ? s.text : 'text-gray-300'}`}>
                    {row[s.value]}
                  </td>
                ))}
                <td className="px-4 py-2 text-center font-medium text-[#0b4a71]">{rateOf(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <Modal title={fullName} onClose={onClose} maxWidthClass="max-w-2xl">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm text-gray-600">
          <span>
            {getGradeLevelLabel(student.grade_level)} - {student.section || '—'}
            {student.lrn ? <span className="text-gray-400"> · LRN {student.lrn}</span> : null}
          </span>
          <span>All attendance{subjectName ? ` · ${subjectName}` : ' · All subjects'}</span>
        </div>

        {error ? (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
        ) : records === null ? (
          <p className="text-center text-gray-500 py-10">Loading…</p>
        ) : subjectIds.length === 0 ? (
          <p className="text-center text-gray-500 py-10">No subjects are assigned to this section yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {statuses.map((s) => (
                <div key={s.value} className="rounded-md border border-gray-200 px-3 py-2">
                  <p className="text-xs text-gray-500">{s.label}</p>
                  <p className={`text-2xl font-bold leading-tight ${s.text}`}>{overall[s.value]}</p>
                </div>
              ))}
              <div className="col-span-2 sm:col-span-1 rounded-md border border-gray-200 px-3 py-2">
                <p className="text-xs text-gray-500">Attendance Rate</p>
                <p className="text-2xl font-bold leading-tight text-[#0b4a71]">{rateOf(overall)}</p>
              </div>
            </div>

            {records.length === 0 ? (
              <p className="text-center text-gray-500 py-6">No attendance has been recorded for this student yet.</p>
            ) : (
              <>
                {renderCountTable('By Month', 'Month', [...byMonth])}
                {renderCountTable(
                  'By Subject',
                  'Subject',
                  [...bySubject],
                  subjectLink ? (subjectId) => subjectLink(subjectId, student) : undefined
                )}

                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setShowRecords((open) => !open)}
                    aria-expanded={showRecords}
                    className="mx-auto flex items-center gap-1 rounded-md border border-[#4ea6d0] px-4 py-1.5 text-sm font-medium text-[#0b4a71] hover:bg-gray-50"
                  >
                    {showRecords ? 'Hide All Records' : 'View All Records'}
                    <ChevronDownIcon className={`h-4 w-4 transition-transform ${showRecords ? 'rotate-180' : ''}`} />
                  </button>
                  {showRecords && (
                    <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200">
                      <table className="w-full text-sm">
                        <tbody className="divide-y divide-gray-100">
                          {records.map((r) => {
                            const status = statusOf[r.status];
                            return (
                              <tr key={`${r.date}|${r.subjectId}`}>
                                <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{formatDate(r.date)}</td>
                                <td className="px-4 py-2 text-gray-700">{subjectNames[r.subjectId] ?? '—'}</td>
                                <td className={`px-4 py-2 text-right font-medium ${status?.text ?? ''}`}>{status?.label ?? r.status}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}

        <div className="flex items-center justify-between pt-1">
          <button type="button" onClick={() => onIndexChange(index - 1)} disabled={!hasPrev} className={navButton}>
            <ChevronLeftIcon className="h-4 w-4" />
            Previous
          </button>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">
              Student {index + 1} of {students.length}
            </span>
            <button type="button" onClick={() => onIndexChange(index + 1)} disabled={!hasNext} className={navButton}>
              Next
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default StudentAttendanceModal;
